import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { lzFGetCurrentRoutePathSegments, lzFLogInfo } from '@lz/services/Utils';
import lzSLayoutNavOpenAtom from '@lz-atoms/Layout/navOpen';
import lzSLayoutThemePaletteModeAtom from '@lz-atoms/Layout/themePaletteMode';
import lzSLayoutNavItemsAtom from '@lz-atoms/Layout/navItems';
import { lzFGetFormattedDate } from '@lz/services/Moment';
import { lzFKebabToWordCase } from '@lz/services/String';
import LzXLayoutPrivate from '@lz-layouts/LayoutPrivate';
import LzXLayoutPublic from '@lz-layouts/LayoutPublic';
import { useLzFGetVisibleNavigation } from '@lz-layouts/Navigation/services';
import { LzTNavItem, LzTNavItems } from '@lz-layouts/Navigation/types';
import LzXButton from '../../forms/Button';
import LzXPoweredByLogo from '@lz-layouts/PoweredByLogo';
import LzSApp from '@lz/app/App';
import LzPTemplate from '../type';
import LzXModernWithLeftNavTemplateNavBar from './NavBar';
import LzXModernWithLeftNavTemplateDrawerList from './DrawerList';

import './styles.scss';

export default function LzXModernWithLeftNavTemplate(props: LzPTemplate) {
  const [layoutNavOpenState, setLayoutNavOpenState] = useAtom(lzSLayoutNavOpenAtom);
  const [layoutThemePaletteModeState, setLayoutThemePaletteModeState] = useAtom(lzSLayoutThemePaletteModeAtom);
  const layoutNavItemsState = useAtomValue(lzSLayoutNavItemsAtom);

  const getVisibleNavigation = useLzFGetVisibleNavigation();

  const navItems = getVisibleNavigation(props.navItems || layoutNavItemsState);

  const [settingsPaneVisible, setSettingsPaneVisible] = useState(false);

  const navigate = useNavigate();

  const toggleNavOpen = () => setLayoutNavOpenState(!layoutNavOpenState);

  const navItemClick = (item: LzTNavItem, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (item.onClick) {
      item.onClick(event);
    }
    if (item.route && item.route !== '') {
      navigate(item.route);
    }
  };

  const currPaths = lzFGetCurrentRoutePathSegments();

  lzFLogInfo('Wrapper Component: ', props.private ? 'PRIVATE' : 'PUBLIC');
  const WrapperComponent = props.private ? LzXLayoutPrivate : LzXLayoutPublic;

  let pathTrack = '/';

  let accountItemsInNav: LzTNavItems | undefined = navItems.filter(
    (item) => 'user' === item.key
  );
  if (accountItemsInNav.length > 0) {
    accountItemsInNav = accountItemsInNav[0]?.children || [];
  } else {
    accountItemsInNav = undefined;
  }

  return (
    <WrapperComponent
      className={'lz-modern-with-left-nav-template ' + (props.className || '')}
      style={props.style}
    >

      <LzXModernWithLeftNavTemplateNavBar
        navOpen={layoutNavOpenState}
        onNavToggle={toggleNavOpen}
        onSettingsPangeToggle={() =>
          setSettingsPaneVisible(!settingsPaneVisible)
        }
        accountMenuItems={accountItemsInNav}
        onAccountMenuItemClick={navItemClick}
      />

      <div className='lz-drawer-wrapper'>
        <Drawer variant='persistent' anchor='left' open={layoutNavOpenState}>
          <IconButton
            color='inherit'
            size='large'
            style={{ position: 'absolute', top: 9, right: 0 }}
            onClick={toggleNavOpen}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={LzSApp.logo_image}
            alt='Logo'
            className='lz-drawer-header-logo'
          />
          <Divider />
          <div className='lz-drawer-nav'>
            <LzXModernWithLeftNavTemplateDrawerList
              level={0}
              parentKeyBreadcrumb=''
              navItems={navItems}
              onItemClick={navItemClick}
            />
          </div>
        </Drawer>

        <Drawer
          variant='temporary'
          anchor='right'
          className='lz-layout-with-nav-modern-settings-pane'
          open={settingsPaneVisible}
          onClose={() => setSettingsPaneVisible(false)}
        >
          <Typography variant='h4'>Settings</Typography>
          <IconButton
            color='inherit'
            size='large'
            style={{ position: 'absolute', top: 9, right: 0 }}
            onClick={() => setSettingsPaneVisible(false)}
          >
            <CloseIcon />
          </IconButton>

          <Divider />

          {layoutThemePaletteModeState === 'dark' ? (
            <LzXButton
              variant='text'
              size='small'
              onClick={() => setLayoutThemePaletteModeState('light')}
            >
              <Brightness7Icon /> Change to Light Mode
            </LzXButton>
          ) : (
            <LzXButton
              variant='text'
              size='small'
              onClick={() => setLayoutThemePaletteModeState('dark')}
            >
              <Brightness4Icon /> Change to Dark Mode
            </LzXButton>
          )}
        </Drawer>

        <main className={'lz-main' + (layoutNavOpenState ? ' drawer-open' : '')}>
          {props.mainTopBar && (
            <div className='main-topbar'>{props.mainTopBar}</div>
          )}

          <div className='main-header'>
            <h1>{props.screenTitle || ''}</h1>
            <Breadcrumbs aria-label='breadcrumb'>
              <Link to='/'>Home</Link>
              {currPaths.map((path: string, dex: number) => {
                pathTrack += path + '/';
                if (
                  props.breadcrumbs &&
                  props.breadcrumbs[dex] &&
                  props.breadcrumbs[dex]?.title
                ) {
                  return (
                    <Link
                      to={props.breadcrumbs[dex]?.route || '/'}
                      key={'path-' + dex}
                    >
                      {props.breadcrumbs[dex]?.title}
                    </Link>
                  );
                }
                return (
                  <Link to={pathTrack} key={'path-' + dex}>
                    {lzFKebabToWordCase(path)}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </div>
          <div className='main-content'>{props.children}</div>
          <div className='main-footer'>
            <div>
              Copyright &copy; {lzFGetFormattedDate('', 'Y')} - All Rights
              Reserved.
            </div>
            <LzXPoweredByLogo />
          </div>
        </main>
      </div>

    </WrapperComponent>
  );
}
